import { useEffect, useMemo, useState } from "react";
import { Polygon, CircleMarker, Polyline, Popup, Tooltip } from "react-leaflet";
import { api, apiError } from "@/lib/api";
import LeafletMap, { FitBounds, pointInPolygon } from "@/components/map/LeafletMap";
import PhotoGallery from "@/components/content/PhotoGallery";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { MapPin, Megaphone, Milestone, Route, Store, Globe, Utensils, Wrench, Crosshair, Satellite, Loader2, Smartphone, Eye, EyeOff, Trash2, Phone, TriangleAlert, X } from "lucide-react";
import SharePreviewDialog from "@/components/share/SharePreviewDialog";
import { PARTNER_CATEGORY_LABEL, PARTNER_TYPE_LABEL } from "@/lib/partnerTypes";
import { toast } from "sonner";

const META = {
  attraction: { color: "#10B981", label: "Attractions", icon: MapPin },
  advertiser: { color: "#F59E0B", label: "Advertisers", icon: Megaphone },
  detour: { color: "#8B5CF6", label: "Detours", icon: Milestone },
  route: { color: "#22D3EE", label: "Routes", icon: Route },
};
const SHOP_COLORS = ["#10B981", "#F59E0B", "#8B5CF6", "#EF4444", "#3B82F6", "#EC4899", "#14B8A6", "#F97316"];
function shopColor(id) {
  if (!id) return "#94A3B8";
  let h = 0; for (const c of id) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return SHOP_COLORS[h % SHOP_COLORS.length];
}
const catHas = (c, kw) => (c.category || "").toLowerCase().includes(kw);

const FILTERS = [
  { key: "shops", label: "Shops", icon: Store },
  { key: "attractions", label: "Attractions", icon: MapPin },
  { key: "food", label: "Food", icon: Utensils },
  { key: "services", label: "Services", icon: Wrench },
  { key: "advertisers", label: "Advertisers", icon: Megaphone },
  { key: "detours", label: "Detours", icon: Milestone },
  { key: "routes", label: "Routes", icon: Route },
  { key: "global", label: "Global Content", icon: Globe },
];
const ALL_ON = FILTERS.reduce((a, f) => ({ ...a, [f.key]: true }), {});

export default function RiderPreview({ publicMode = false, initialPathId = null, initialZoneId = null }) {
  const [paths, setPaths] = useState([]);
  const [pathId, setPathId] = useState("");
  const [zones, setZones] = useState([]);
  const [zoneFilter, setZoneFilter] = useState(initialZoneId || "all");
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [satellite, setSatellite] = useState(false);
  const [filters, setFilters] = useState({ ...ALL_ON });
  const [selected, setSelected] = useState(null);
  const [nonce, setNonce] = useState(0);
  const [shops, setShops] = useState([]);
  const [trailRoutes, setTrailRoutes] = useState([]);
  const [shownRouteIds, setShownRouteIds] = useState(() => new Set());

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/rider/bike-paths"); setPaths(data); if (data.length) setPathId(initialPathId || data[0].id); }
      catch (e) { toast.error(apiError(e.response?.data?.detail)); }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => { api.get("/rider/shops").then(({ data }) => setShops(data)).catch(() => {}); }, []);

  const reload = async () => {
    if (!pathId) return;
    try {
      const [c, z, r] = await Promise.all([
        api.get("/rider/content", { params: { bike_path_id: pathId } }),
        api.get("/rider/zones", { params: { bike_path_id: pathId } }),
        api.get("/rider/routes", { params: { bike_path_id: pathId } }),
      ]);
      setContent(c.data); setZones(z.data); setTrailRoutes(r.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { reload(); setSelected(null); setShownRouteIds(new Set()); }, [pathId]);

  const path = useMemo(() => paths.find((p) => p.id === pathId), [paths, pathId]);
  const zoneById = useMemo(() => Object.fromEntries(zones.map((z) => [z.id, z])), [zones]);

  const isVisible = (c) => {
    if (c.scope === "global" && !filters.global) return false;
    if (zoneFilter !== "all") { if (c.scope === "global") return false; if (c.zone_id !== zoneFilter) return false; }
    const kindOn = { attraction: filters.attractions, advertiser: filters.advertisers, detour: filters.detours, route: filters.routes }[c.kind];
    if (!kindOn) return false;
    if (!filters.food && catHas(c, "food")) return false;
    if (!filters.services && catHas(c, "service")) return false;
    return true;
  };
  const outsidePoint = (c) => {
    if (c.scope !== "local" || !c.zone_id) return false;
    const z = zoneById[c.zone_id]; if (!z || c.lat == null) return false;
    return !pointInPolygon(c.lat, c.lng, z.polygon || []);
  };
  const routeSegments = (c) => {
    const z = c.scope === "local" ? zoneById[c.zone_id] : null;
    const segs = [];
    for (let i = 0; i < (c.path?.length || 0) - 1; i++) {
      const a = c.path[i], b = c.path[i + 1];
      const inside = !z || (pointInPolygon(a[0], a[1], z.polygon || []) && pointInPolygon(b[0], b[1], z.polygon || []));
      segs.push({ pts: [a, b], inside });
    }
    return segs;
  };

  const visible = content.filter(isVisible);
  const points = visible.filter((c) => c.kind !== "route" && c.lat != null);
  const routes = visible.filter((c) => c.kind === "route");
  const shownZones = filters.shops ? zones.filter((z) => zoneFilter === "all" || z.id === zoneFilter) : [];
  // Routes tied to a specific stop reveal on tap; all other approved routes
  // (admin primary routes AND partner-created routes, which have no linked_stop_id) render always.
  const alwaysRoutes = trailRoutes.filter((r) => !r.linked_stop_id && (r.coordinates?.length || 0) >= 2);
  const stopRoutes = trailRoutes.filter((r) => r.linked_stop_id && (r.coordinates?.length || 0) >= 2);
  const routeForStop = (stopId) => stopRoutes.find((r) => r.linked_stop_id === stopId);
  const toggleRoute = (id) => setShownRouteIds((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const hide = async (c) => { try { await api.patch(`/content/${c.id}/visibility`); toast.success(c.visible === false ? "Now visible" : "Hidden from riders"); reload(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const del = async (c) => { try { await api.delete(`/content/${c.id}`); toast.success("Deleted"); setSelected(null); reload(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const markGlobal = async (c) => { try { await api.patch(`/content/${c.id}/scope`, { scope: "global" }); toast.success("Marked as global"); reload(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  if (!paths.length) return <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center text-muted-foreground" data-testid="rider-no-paths">{publicMode ? "This preview is not available." : "Create a bike path to preview the rider experience."}</div>;

  const Card = ({ c, compact }) => {
    const outside = outsidePoint(c);
    return (
      <div className={`${compact ? "" : "rounded-xl border border-border bg-card p-3"}`} data-testid={`rider-card-${c.id}`}>
        {c.photos?.length > 0 && <div className="mb-2 rounded-lg overflow-hidden"><PhotoGallery photos={c.photos} height={compact ? 96 : 120} rounded testId={`rider-gallery-${c.id}`} /></div>}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded text-white" style={{ background: META[c.kind].color }}>{c.kind}</span>
          <span className="text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">{c.scope}</span>
          {outside && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-500/15 text-red-500 flex items-center gap-1"><TriangleAlert className="w-3 h-3" />Outside zone</span>}
        </div>
        <div className="font-heading font-bold text-sm mt-1">{c.name}</div>
        {c.category && <div className="text-xs text-primary">{c.category}</div>}
        {c.description && <p className="text-xs text-muted-foreground mt-1">{c.description}</p>}
        {c.kind === "route" && <div className="text-xs text-muted-foreground mt-1">{c.difficulty || "—"}{c.est_time ? ` · ${c.est_time}` : ""}</div>}
        {c.website && <a href={c.website} target="_blank" rel="noreferrer" className="text-xs text-primary flex items-center gap-1 mt-1"><Globe className="w-3 h-3" />Website</a>}
        {c.phone && <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3" />{c.phone}</div>}
        {(() => { const rr = routeForStop(c.id); if (!rr) return null; const on = shownRouteIds.has(rr.id); return (
          <Button size="sm" variant={on ? "default" : "outline"} className="mt-2 w-full" style={on ? { background: rr.color, borderColor: rr.color, color: "#fff" } : { borderColor: rr.color, color: rr.color }} onClick={() => toggleRoute(rr.id)} data-testid={`rider-show-route-${c.id}`}>
            <Route className="w-3.5 h-3.5 mr-1" />{on ? "Hide route" : "Show route"}
          </Button>
        ); })()}
        {!publicMode && (
          <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-border">
            <Button size="sm" variant="outline" onClick={() => hide(c)} data-testid={`rider-hide-${c.id}`}>{c.visible === false ? <EyeOff className="w-3.5 h-3.5 mr-1" /> : <Eye className="w-3.5 h-3.5 mr-1" />}{c.visible === false ? "Show" : "Hide"}</Button>
            {c.scope === "local" && (
              <AlertDialog>
                <AlertDialogTrigger asChild><Button size="sm" variant="outline" data-testid={`rider-global-${c.id}`}><Globe className="w-3.5 h-3.5 mr-1" />Mark Global</Button></AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Make "{c.name}" global?</AlertDialogTitle><AlertDialogDescription>It will no longer belong to a shop or zone and will show to all riders on this bike path.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => markGlobal(c)} data-testid={`rider-confirm-global-${c.id}`}>Mark Global</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="text-destructive ml-auto" data-testid={`rider-delete-${c.id}`}><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader><AlertDialogTitle>Delete "{c.name}"?</AlertDialogTitle><AlertDialogDescription>Admin override — permanently removes this content. Cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(c)} data-testid={`rider-confirm-delete-${c.id}`}>Delete</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="rider-preview">
      {publicMode ? (
        <h1 className="font-heading text-2xl font-bold" data-testid="rider-public-title">{path?.name || "Rider Preview"}{path?.description ? <span className="block text-sm font-normal text-muted-foreground mt-0.5">{path.description}</span> : null}</h1>
      ) : (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Smartphone className="w-4 h-4 text-primary" />A read-only, Rider-style preview of exactly what riders see for this bike path.</div>
      )}

      <div className="flex flex-wrap items-end gap-4">
        {!publicMode && (
          <div className="w-56">
            <Label>Bike path</Label>
            <Select value={pathId} onValueChange={setPathId}>
              <SelectTrigger data-testid="rider-path-select"><SelectValue /></SelectTrigger>
              <SelectContent>{paths.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <div className="w-56">
          <Label>Zone</Label>
          <Select value={zoneFilter} onValueChange={setZoneFilter}>
            <SelectTrigger data-testid="rider-zone-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All zones</SelectItem>{zones.map((z) => <SelectItem key={z.id} value={z.id}>{z.name}{z.shop_name ? ` · ${z.shop_name}` : ""}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2"><Satellite className="w-4 h-4 text-muted-foreground" /><Switch checked={satellite} onCheckedChange={setSatellite} data-testid="rider-toggle-satellite" /><span className="text-sm">Satellite</span></div>
        <Button variant="outline" size="sm" onClick={() => setNonce((n) => n + 1)} data-testid="rider-recenter"><Crosshair className="w-4 h-4 mr-1" />Recenter</Button>
        {!publicMode && <SharePreviewDialog title={path?.name} triggerLabel="Share" triggerTestId="rider-share" url={`${window.location.origin}/preview/bike-path/${pathId}${zoneFilter !== "all" ? `?zone=${zoneFilter}` : ""}`} />}
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="rider-filters">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilters((s) => ({ ...s, [f.key]: !s[f.key] }))} data-testid={`rider-filter-${f.key}`}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${filters[f.key] ? "bg-primary/15 text-primary border-primary/30" : "text-muted-foreground border-border"}`}>
            <f.icon className="w-3.5 h-3.5" />{f.label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1fr_340px] gap-4">
        <LeafletMap height={560} satellite={satellite} center={path?.boundary?.[0] || [41.75, -71.3]}>
          {path?.boundary?.length >= 3 && <Polygon positions={path.boundary} pathOptions={{ color: satellite ? "#fff" : "#334155", weight: 2, fillOpacity: 0.04 }} />}
          {path?.boundary?.length >= 2 && <FitBounds bounds={path.boundary} nonce={nonce} />}
          {shownZones.map((z) => (
            (z.polygon?.length >= 3) && <Polygon key={z.id} positions={z.polygon} pathOptions={{ color: shopColor(z.shop_id), weight: 2, fillOpacity: 0.12 }}>
              <Tooltip>{z.shop_name ? `${z.name} · ${z.shop_name}` : `${z.name} · Unassigned`}</Tooltip>
            </Polygon>
          ))}
          {routes.map((c) => routeSegments(c).map((s, i) => (
            <Polyline key={`${c.id}-${i}`} positions={s.pts} pathOptions={{ color: s.inside ? META.route.color : "#EF4444", weight: 4, dashArray: s.inside ? null : "6 8" }} eventHandlers={{ click: () => setSelected(c) }}>
              <Tooltip>{s.inside ? c.name : "Route segment outside assigned zone"}</Tooltip>
            </Polyline>
          )))}
          {routes.map((c) => (c.waypoint_labels || []).map((wl, i) => (wl && wl.name && c.path[i]) ? (
            <CircleMarker key={`${c.id}-wp-${i}`} center={c.path[i]} radius={5} pathOptions={{ color: "#fff", weight: 2, fillColor: "#0EA5E9", fillOpacity: 1 }}>
              <Tooltip permanent direction="top">{wl.name}</Tooltip>
            </CircleMarker>
          ) : null))}
          {alwaysRoutes.map((r) => (
            <Polyline key={`pr-${r.id}`} positions={r.coordinates} pathOptions={{ color: r.color, weight: 6, opacity: 0.9 }}>
              <Tooltip>{r.name}</Tooltip>
            </Polyline>
          ))}
          {stopRoutes.filter((r) => shownRouteIds.has(r.id)).map((r) => (
            <Polyline key={`cr-${r.id}`} positions={r.coordinates} pathOptions={{ color: r.color, weight: 5, opacity: 0.95, dashArray: "1 0" }}>
              <Tooltip>{r.name}</Tooltip>
            </Polyline>
          ))}
          {points.map((c) => {
            const outside = outsidePoint(c);
            return (
              <CircleMarker key={c.id} center={[c.lat, c.lng]} radius={8}
                pathOptions={{ color: "#fff", weight: 2, fillColor: outside ? "#EF4444" : META[c.kind].color, fillOpacity: 1 }}
                eventHandlers={{ click: () => setSelected(c) }}>
                <Tooltip>{outside ? `${c.name} — Outside assigned zone` : c.name}</Tooltip>
              </CircleMarker>
            );
          })}
          {filters.shops && shops.map((s) => (s.lat != null && s.lng != null) ? (
            <CircleMarker key={`shop-${s.id}`} center={[s.lat, s.lng]} radius={9}
              pathOptions={{ color: "#fff", weight: 2, fillColor: shopColor(s.id), fillOpacity: 1 }}>
              <Popup>
                <div className="min-w-[170px] space-y-1" data-testid={`shop-popup-${s.id}`}>
                  <div className="font-heading font-bold text-sm">{s.name}</div>
                  {s.partner_type && <span data-testid={`shop-partner-badge-${s.id}`} className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${s.partner_type === "trail" ? "bg-emerald-500/15 text-emerald-600" : "bg-blue-500/15 text-blue-600"}`}>{PARTNER_CATEGORY_LABEL[s.partner_category] || PARTNER_TYPE_LABEL[s.partner_type] || "Partner"}</span>}
                  {s.description && <p className="text-xs text-muted-foreground">{s.description}</p>}
                  {s.address && <p className="text-xs text-muted-foreground">{s.address}</p>}
                  {s.website && <a href={s.website} target="_blank" rel="noreferrer" className="text-xs text-primary underline block">Website</a>}
                  {s.booking_url && <a href={s.booking_url} target="_blank" rel="noreferrer" data-testid={`shop-book-rental-${s.id}`} className="inline-block mt-1 px-3 py-1.5 rounded-md bg-primary text-white text-xs font-semibold no-underline">Book a Rental</a>}
                </div>
              </Popup>
              <Tooltip>{s.name}</Tooltip>
            </CircleMarker>
          ) : null)}
        </LeafletMap>

        <div className="rounded-xl border border-border bg-card flex flex-col max-h-[560px]" data-testid="rider-sidebar">
          {selected ? (
            <div className="p-3 border-b border-border">
              <div className="flex items-center justify-between mb-2"><span className="text-xs font-semibold text-muted-foreground">Selected</span><Button size="sm" variant="ghost" onClick={() => setSelected(null)} data-testid="rider-clear-selected"><X className="w-4 h-4" /></Button></div>
              <Card c={content.find((x) => x.id === selected.id) || selected} compact />
            </div>
          ) : null}
          <div className="px-3 py-2 border-b border-border text-xs font-semibold text-muted-foreground">Visible content · {visible.length}</div>
          <div className="overflow-y-auto p-2 space-y-1.5">
            {visible.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground" data-testid="rider-list-empty">Nothing matches the current filters.</div> : visible.map((c) => {
              const outside = outsidePoint(c);
              return (
                <button key={c.id} onClick={() => setSelected(c)} data-testid={`rider-list-${c.id}`}
                  className="w-full text-left flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-secondary transition-colors">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: outside ? "#EF4444" : META[c.kind].color }} />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium truncate">{c.name}{c.visible === false ? " (hidden)" : ""}</span>
                    <span className="block text-xs text-muted-foreground truncate capitalize">{c.kind} · {c.scope}{c.category ? ` · ${c.category}` : ""}</span>
                  </span>
                  {outside && <TriangleAlert className="w-3.5 h-3.5 text-red-500 shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
